import type { JSX } from "react";
import { useRef, useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { ulid } from "ulid";
import { Button } from "@/ui/button.js";
import { Card, CardContent } from "@/ui/card.js";
import { PHOTO_IMPORT_MODEL } from "@recipevault/shared";
import {
  validateInputFile,
  resizeImage,
  uploadTriple,
  isSafari,
} from "../../shared/photos.js";
import { presignPhotoImport, submitImport } from "../../features/imports/api.js";

const MAX_PAGES = 8;

interface Page {
  id: string;
  file: File;
  previewUrl: string;
}

export function PhotoCapture(): JSX.Element {
  const navigate = useNavigate();
  const [pages, setPages] = useState<Page[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState<string>("");
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);

  function addFiles(files: FileList | null): void {
    if (!files || files.length === 0) return;
    const safari = isSafari();
    const accepted: Page[] = [];
    for (const file of Array.from(files)) {
      const v = validateInputFile(file, { isSafari: safari });
      if (!v.ok) {
        toast.error(v.message);
        continue;
      }
      accepted.push({
        id: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2)}`,
        file,
        previewUrl: URL.createObjectURL(file),
      });
    }
    setPages((prev) => {
      const combined = [...prev, ...accepted];
      if (combined.length > MAX_PAGES) {
        toast.error(`Up to ${MAX_PAGES} pages per recipe — extra pages weren't added.`);
        combined.slice(MAX_PAGES).forEach((p) => URL.revokeObjectURL(p.previewUrl));
        return combined.slice(0, MAX_PAGES);
      }
      return combined;
    });
  }

  function removePage(id: string): void {
    setPages((prev) => {
      const p = prev.find((x) => x.id === id);
      if (p) URL.revokeObjectURL(p.previewUrl);
      return prev.filter((x) => x.id !== id);
    });
  }

  function movePage(id: string, dir: -1 | 1): void {
    setPages((prev) => {
      const i = prev.findIndex((x) => x.id === id);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= prev.length) return prev;
      const copy = [...prev];
      const a = copy[i]!;
      const b = copy[j]!;
      copy[i] = b;
      copy[j] = a;
      return copy;
    });
  }

  async function onSubmit(): Promise<void> {
    if (pages.length === 0 || submitting) return;
    setSubmitting(true);
    try {
      const photoKeys: string[] = [];
      for (let i = 0; i < pages.length; i++) {
        setProgress(`Uploading page ${i + 1} of ${pages.length}…`);
        const blobs = await resizeImage(pages[i]!.file, ["thumb", "full", "extract"]);
        const presigned = await presignPhotoImport();
        await uploadTriple(
          { thumb: blobs.thumb, full: blobs.full, extract: blobs.extract! },
          {
            thumbUrl: presigned.thumbUrl,
            fullUrl: presigned.fullUrl,
            extractUrl: presigned.extractUrl,
          },
        );
        photoKeys.push(presigned.photoKey);
      }
      setProgress("Reading pages…");
      const { jobId } = await submitImport({ kind: "photo", photoKeys, model: PHOTO_IMPORT_MODEL }, ulid());
      pages.forEach((p) => URL.revokeObjectURL(p.previewUrl));
      await navigate(`/imports/${encodeURIComponent(jobId)}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Photo import failed");
      setSubmitting(false);
      setProgress("");
    }
  }

  return (
    <div className="mx-auto max-w-xl px-4 py-6 space-y-6">
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold">Photograph a recipe</h1>
        <p className="text-sm text-muted-foreground">
          Snap or pick up to {MAX_PAGES} pages of one recipe. We read the ingredients and steps
          for you — then you confirm before it's saved.
        </p>
      </div>

      {/* Hidden inputs: camera (rear-facing) + gallery (multi-select). */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        aria-label="Take photo"
        className="hidden"
        onChange={(e) => {
          addFiles(e.target.files);
          e.target.value = "";
        }}
      />
      <input
        ref={galleryInputRef}
        type="file"
        accept="image/*"
        multiple
        aria-label="Choose from library"
        className="hidden"
        onChange={(e) => {
          addFiles(e.target.files);
          e.target.value = "";
        }}
      />

      <div className="flex gap-3">
        <Button
          type="button"
          variant="outline"
          className="flex-1"
          disabled={submitting || pages.length >= MAX_PAGES}
          onClick={() => cameraInputRef.current?.click()}
        >
          📷 Take photo
        </Button>
        <Button
          type="button"
          variant="outline"
          className="flex-1"
          disabled={submitting || pages.length >= MAX_PAGES}
          onClick={() => galleryInputRef.current?.click()}
        >
          🖼️ Choose from library
        </Button>
      </div>

      {pages.length > 0 && (
        <ul className="grid grid-cols-3 gap-3" aria-label="Pages to extract">
          {pages.map((page, i) => (
            <li key={page.id}>
              <Card className="overflow-hidden">
                <CardContent className="p-0">
                  <div className="relative">
                    <img
                      src={page.previewUrl}
                      alt={`Page ${i + 1}`}
                      className="aspect-square w-full object-cover"
                    />
                    <span className="absolute left-1 top-1 rounded bg-black/70 px-1.5 text-xs font-medium text-white">
                      {i + 1}
                    </span>
                    <button
                      type="button"
                      aria-label={`Remove page ${i + 1}`}
                      disabled={submitting}
                      onClick={() => removePage(page.id)}
                      className="absolute right-1 top-1 rounded bg-black/70 px-1.5 text-xs text-white"
                    >
                      ✕
                    </button>
                  </div>
                  <div className="flex justify-between p-1">
                    <button
                      type="button"
                      aria-label={`Move page ${i + 1} earlier`}
                      disabled={submitting || i === 0}
                      onClick={() => movePage(page.id, -1)}
                      className="px-2 text-muted-foreground disabled:opacity-30"
                    >
                      ←
                    </button>
                    <button
                      type="button"
                      aria-label={`Move page ${i + 1} later`}
                      disabled={submitting || i === pages.length - 1}
                      onClick={() => movePage(page.id, 1)}
                      className="px-2 text-muted-foreground disabled:opacity-30"
                    >
                      →
                    </button>
                  </div>
                </CardContent>
              </Card>
            </li>
          ))}
        </ul>
      )}

      <div className="space-y-2">
        <Button
          type="button"
          className="w-full"
          disabled={pages.length === 0 || submitting}
          onClick={() => {
            void onSubmit();
          }}
        >
          {submitting
            ? (progress || "Working…")
            : pages.length === 0
              ? "Add a page to start"
              : `Extract recipe (${pages.length} ${pages.length === 1 ? "page" : "pages"})`}
        </Button>
        <p className="text-xs text-muted-foreground">
          JPEG, PNG, WebP, or HEIC. Photos are resized and stripped of metadata in your browser.
        </p>
      </div>
    </div>
  );
}
